import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  BarChart3,
  Users,
  GraduationCap,
  Mail,
  Building2,
  MessageSquare,
  Video,
  Search,
  ClipboardList,
  Image,
  FileText,
  Star,
  LogOut,
  Bell,
  Settings,
  User,
  Globe,
  FolderCheck,
  DollarSign,
} from 'lucide-react';
import { USER_ROLES } from '../../constants';
import authService from '../../services/auth';
import apiService from '../../services/api';
import '../../styles/responsive-admin.css';

const ResponsiveLayout = ({
  children,
  user,
  activeSection,
  onSectionChange,
  selectedCountry = 'all',
  onCountryChange,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [countries, setCountries] = useState([]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch countries for dropdown
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiService.getDashboardCountries();
        setCountries(response || []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        isMobile &&
        !event.target.closest('.responsive-sidebar') &&
        !event.target.closest('.responsive-mobile-toggle')
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen, isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  const getMenuItems = useCallback(() => {
    const baseItems = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: BarChart3,
        color: 'var(--primary)',
      },
    ];

    const changePasswordItem = {
      id: 'change-password',
      label: 'Change Password',
      icon: Settings,
      color: 'var(--warning)',
    };

    if (user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.ADMIN) {
      return [
        ...baseItems,
        {
          id: 'referral-partners',
          label: 'Business Partners',
          icon: Users,
          color: 'var(--accent)',
        },
        {
          id: 'team-leads',
          label: 'Academic Counsellors',
          icon: User,
          color: 'var(--secondary)',
        },
        {
          id: 'admissions',
          label: 'Admission Enquiries',
          icon: GraduationCap,
          color: 'var(--success)',
        },
        {
          id: 'enquiries',
          label: 'General Enquiries',
          icon: Mail,
          color: 'var(--danger)',
        },
        {
          id: 'universities',
          label: 'Universities',
          icon: Building2,
          color: 'var(--info)',
        },
        {
          id: 'countries',
          label: 'Countries',
          icon: Building2,
          color: 'var(--secondary)',
        },
        {
          id: 'university-countries',
          label: 'University Countries',
          icon: Building2,
          color: 'var(--accent)',
        },
        {
          id: 'courses',
          label: 'Courses',
          icon: GraduationCap,
          color: 'var(--primary)',
        },
        {
          id: 'testimonials',
          label: 'Testimonials',
          icon: MessageSquare,
          color: 'var(--warning)',
        },
        {
          id: 'gallery',
          label: 'Gallery',
          icon: Image,
          color: 'var(--info)',
        },
        {
          id: 'blogs',
          label: 'Blogs',
          icon: FileText,
          color: 'var(--success)',
        },
        /* {
          id: 'news',
          label: 'News',
          icon: FileText,
          color: 'var(--info)',
        }, */
        {
          id: 'features',
          label: 'Features',
          icon: Star,
          color: 'var(--warning)',
        },
        {
          id: 'task-allocation',
          label: 'Task Allocation',
          icon: ClipboardList,
          color: 'var(--primary)',
        },
        {
          id: 'inbound-task-allocation',
          label: 'In Bound Task Allocation',
          icon: ClipboardList,
          color: 'var(--secondary)',
        },
        {
          id: 'seo',
          label: 'SEO Management',
          icon: Search,
          color: 'var(--accent)',
        },
        changePasswordItem,
      ];
    } else if (user?.role === USER_ROLES.TEAM_LEAD) {
      return [
        ...baseItems,
        {
          id: 'task-allocation',
          label: 'My Tasks',
          icon: ClipboardList,
          color: 'var(--primary)',
        },
        {
          id: 'referral-partners',
          label: 'Business Partners',
          icon: Users,
          color: 'var(--accent)',
        },
        {
          id: 'admissions',
          label: 'Admission Enquiries',
          icon: GraduationCap,
          color: 'var(--success)',
        },
        changePasswordItem,
      ];
    } else if (user?.role === USER_ROLES.REFERRAL_PARTNER) {
      return [
        ...baseItems,
        {
          id: 'admissions',
          label: 'Admission Enquiries',
          icon: GraduationCap,
          color: 'var(--success)',
        },
        changePasswordItem,
      ];
    } else if (
      user?.role === 'DocumentsManager' ||
      user?.role === 'documentation_manager' ||
      user?.role === USER_ROLES.DOCUMENTS_MANAGER
    ) {
      return [
        ...baseItems,
        {
          id: 'documents-manager',
          label: 'Documents Manager',
          icon: FolderCheck,
          color: 'var(--info)',
        },
        changePasswordItem,
      ];
    } else if (
      user?.role === 'AccountsManager' ||
      user?.role === 'accounts_manager' ||
      user?.role === USER_ROLES.ACCOUNTS_MANAGER
    ) {
      return [
        ...baseItems,
        {
          id: 'accounts-manager',
          label: 'Accounts Manager',
          icon: DollarSign,
          color: 'var(--success)',
        },
        changePasswordItem,
      ];
    } else if (user?.role === 'seo_manager' || user?.role === USER_ROLES.SEO_MANAGER) {
      return [
        {
          id: 'seo',
          label: 'SEO Management',
          icon: Search,
          color: 'var(--accent)',
        },
        changePasswordItem,
      ];
    }
    return [...baseItems, changePasswordItem];
  }, [user?.role]);

  const menuItems = getMenuItems();

  return (
    <div className='responsive-admin-container'>
      {/* Skip Link for Accessibility */}
      <a href='#main-content' className='skip-link'>
        Skip to main content
      </a>

      {/* Mobile Toggle Button */}
      {isMobile && (
        <motion.button
          className='responsive-mobile-toggle'
          onClick={toggleSidebar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={sidebarOpen}
        >
          <motion.div animate={{ rotate: sidebarOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </motion.div>
        </motion.button>
      )}

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            className='responsive-overlay active'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        className={`responsive-sidebar ${sidebarOpen ? 'open' : ''}`}
        initial={isMobile ? { x: -280 } : { x: 0 }}
        animate={isMobile ? { x: sidebarOpen ? 0 : -280 } : { x: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      >
        {/* Sidebar Header */}
        <div className='responsive-sidebar-header'>
          <div className='login-header'>
            <img src='/assets/img/logo/logo-white.webp' alt='Logo' style={{ height: '70px' }} />
          </div>
        </div>

        {/* Navigation */}
        <nav className='responsive-nav' role='navigation' aria-label='Main navigation'>
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <motion.button
                key={item.id}
                className={`responsive-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => {
                  onSectionChange(item.id);
                  if (isMobile) {
                    setSidebarOpen(false);
                  }
                }}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.4 }}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <IconComponent size={20} style={{ color: item.color }} />
                </motion.div>
                <span>{item.label}</span>
              </motion.button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div style={{ marginTop: 'auto', padding: 'var(--space-4)' }}>
          {/* User Profile Section */}
          <div
            style={{
              padding: 'var(--space-4)',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius-xl)',
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--gray-200)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: '600',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-900)',
                  }}
                >
                  {user?.name || 'User'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-500)',
                    textTransform: 'capitalize',
                  }}
                >
                  {user?.role?.replace('_', ' ').toLowerCase() || 'User'}
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <motion.button
            className='responsive-btn responsive-btn-secondary'
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              width: '100%',
              background: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--danger)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <LogOut size={16} />
            <span>Logout</span>
          </motion.button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className='responsive-main' id='main-content' role='main'>
        {/* Header */}
        <motion.header
          className='responsive-header'
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {[...menuItems].find((item) => item.id === activeSection)?.label || 'Dashboard'}
            </motion.h1>
          </div>

          {/* Header Actions */}
          {!isMobile && (
            <motion.div
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              {/* Country Selection Dropdown - Only for super_admin and director */}
              {(user?.role === 'super_admin' || user?.role === 'director') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <Globe size={16} style={{ color: 'var(--gray-600)' }} />
                  <select
                    value={selectedCountry}
                    onChange={(e) => onCountryChange?.(e.target.value)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      fontSize: 'var(--text-sm)',
                      fontWeight: '500',
                      color: 'var(--gray-700)',
                      minWidth: '140px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value='all'>All Countries</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* User Menu */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-4)',
                  background: 'var(--gray-100)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                }}
              >
                <User size={16} />
                <span>{user?.name}</span>
              </div>
            </motion.div>
          )}
        </motion.header>

        {/* Mobile Country Selection - Only for super_admin and director */}
        {isMobile && (user?.role === 'super_admin' || user?.role === 'director') && (
          <motion.div
            style={{
              padding: 'var(--space-4)',
              background: 'white',
              borderBottom: '1px solid var(--gray-200)',
              marginBottom: 'var(--space-4)',
            }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Globe size={16} style={{ color: 'var(--gray-600)' }} />
              <select
                value={selectedCountry}
                onChange={(e) => onCountryChange?.(e.target.value)}
                style={{
                  flex: 1,
                  padding: 'var(--space-3)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  background: 'white',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                  color: 'var(--gray-700)',
                }}
              >
                <option value='all'>All Countries</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        )}

        {/* Content Body */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <AnimatePresence mode='wait'>
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              transition={{ duration: 0.3, type: 'spring' }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
};

export default ResponsiveLayout;
